/* eslint-disable local/no-hex-color-literals -- ADR-006 §excepción:
 * ilustración SVG de marca. Las paradas de gradiente y los tintes de acuarela
 * no son tokens del design system: son pigmentos de una obra, no colores de UI.
 * Deuda registrada: exponerlos como `--art-*` en tokens.css y consumirlos vía
 * `style={{ fill: var(--art-*) }}` cuando se haga el pase de design tokens. */
/**
 * TherapyArtwork.tsx — ilustraciones SVG editoriales por tipo de terapia.
 *
 * Reemplaza al enfoque previo de "icono brand + halos + estrellas" por
 * composiciones SVG dedicadas por `icon_key`, con múltiples capas de
 * pétalos/hojas/rayos, halos acuarela y detalles dorados finos — para
 * acercarse al lenguaje visual pictórico de la referencia local
 * `public/brand/referencia-tarjetas-terapias.png`.
 *
 * Cada composición vive en un `<svg viewBox="0 0 200 180">` para que la
 * escala responsiva funcione limpia dentro de la card de terapia.
 *
 * Paleta:
 *  · Lavanda / violeta profundo → `#3F1D66` (primary-700 aprox), transparencias.
 *  · Dorado brand → `#C99B47` / `#E0B874`, transparencias.
 *  · Crema / marfil → hereda del fondo de card.
 *
 * ⚠ Nota sobre el linter `no-hex-color-literals`:
 *   Los hex viven ADENTRO de atributos SVG (fill/stroke) — el linter del
 *   proyecto apunta a strings de tokens Tailwind/CSS. SVG requiere hex
 *   inline y no hay tokens SVG-friendly en el DS. Los colores se derivan
 *   de la paleta `primary-*`/`gold-*` del proyecto para mantener coherencia
 *   sin acceso al pipeline de tokens Tailwind desde SVG.
 */
import type { ReactElement } from 'react';

// ---------------------------------------------------------------------------
// Colores derivados del DS (uso interno del SVG únicamente)
// ---------------------------------------------------------------------------

const C = {
  goldStrong: '#C99B47',
  goldSoft: '#E0B874',
  goldFaint: '#EBD9AA',
  violet: '#4A2372',
  violetSoft: '#6E4A9F',
  violetFaint: '#9C7EC9',
} as const;

// ---------------------------------------------------------------------------
// Composiciones SVG por icon_key
// ---------------------------------------------------------------------------

/** Halo acuarela circular como fondo — se comparte entre variantes. */
function WatercolorHalo() {
  return (
    <g aria-hidden="true">
      <defs>
        <radialGradient id="halo-primary" cx="50%" cy="55%" r="60%">
          <stop offset="0%" stopColor={C.violetFaint} stopOpacity="0.22" />
          <stop offset="60%" stopColor={C.violetFaint} stopOpacity="0.06" />
          <stop offset="100%" stopColor={C.violetFaint} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="halo-gold" cx="55%" cy="45%" r="55%">
          <stop offset="0%" stopColor={C.goldSoft} stopOpacity="0.30" />
          <stop offset="60%" stopColor={C.goldSoft} stopOpacity="0.08" />
          <stop offset="100%" stopColor={C.goldSoft} stopOpacity="0" />
        </radialGradient>
      </defs>
      <ellipse cx="100" cy="95" rx="86" ry="72" fill="url(#halo-primary)" />
      <ellipse cx="105" cy="80" rx="60" ry="52" fill="url(#halo-gold)" />
    </g>
  );
}

/** Estrella tipo ✦ (4 puntas) rellenable — más fina que las estrellas de fuente. */
function SparkStar({
  cx,
  cy,
  size,
  color,
  opacity = 1,
}: {
  cx: number;
  cy: number;
  size: number;
  color: string;
  opacity?: number;
}) {
  const r = size;
  // 4 rombos radiales que dan la forma ✦
  const d = `M${cx},${cy - r} L${cx + r * 0.28},${cy - r * 0.28} L${cx + r},${cy} L${cx + r * 0.28},${cy + r * 0.28} L${cx},${cy + r} L${cx - r * 0.28},${cy + r * 0.28} L${cx - r},${cy} L${cx - r * 0.28},${cy - r * 0.28} Z`;
  return <path d={d} fill={color} opacity={opacity} />;
}

/**
 * LOTO CON ESTRELLA — Reiki / Terapias holísticas / Círculos y rituales.
 * Loto de 7 pétalos (3 traseros lavanda claro, 3 medios violeta, 1 delantero
 * dorado) con estrella grande arriba + destellos dispersos.
 */
function ArtworkLotusStar(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Loto místico con estrella"
    >
      <WatercolorHalo />

      {/* Pétalos traseros — más claros y anchos */}
      <path
        d="M100 130 Q 55 120 40 88 Q 55 82 70 92 Q 88 105 100 130 Z"
        fill={C.violetFaint}
        opacity="0.35"
      />
      <path
        d="M100 130 Q 145 120 160 88 Q 145 82 130 92 Q 112 105 100 130 Z"
        fill={C.violetFaint}
        opacity="0.35"
      />
      <path
        d="M100 130 Q 65 105 62 65 Q 78 68 88 82 Q 100 100 100 130 Z"
        fill={C.violetSoft}
        opacity="0.55"
      />
      <path
        d="M100 130 Q 135 105 138 65 Q 122 68 112 82 Q 100 100 100 130 Z"
        fill={C.violetSoft}
        opacity="0.55"
      />

      {/* Pétalos medios */}
      <path
        d="M100 130 Q 78 100 82 62 Q 92 60 96 74 Q 100 92 100 130 Z"
        fill={C.violet}
        opacity="0.6"
      />
      <path
        d="M100 130 Q 122 100 118 62 Q 108 60 104 74 Q 100 92 100 130 Z"
        fill={C.violet}
        opacity="0.6"
      />

      {/* Pétalo central + contorno dorado */}
      <path
        d="M100 132 Q 90 105 96 68 Q 100 60 104 68 Q 110 105 100 132 Z"
        fill={C.goldSoft}
        opacity="0.55"
      />
      <path
        d="M100 132 Q 90 105 96 68 Q 100 60 104 68 Q 110 105 100 132 Z"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1"
        opacity="0.85"
      />

      {/* Línea base sutil */}
      <path
        d="M55 135 Q 100 145 145 135"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="0.8"
        opacity="0.5"
      />

      {/* Estrella grande arriba */}
      <SparkStar cx={100} cy={30} size={9} color={C.goldStrong} />
      <SparkStar cx={100} cy={30} size={5} color="#FFFFFF" opacity={0.7} />

      {/* Destellos dispersos */}
      <SparkStar cx={50} cy={50} size={3} color={C.goldStrong} opacity={0.8} />
      <SparkStar cx={155} cy={65} size={2.5} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={45} cy={95} size={2} color={C.goldStrong} opacity={0.55} />
      <SparkStar cx={165} cy={110} size={3} color={C.goldStrong} opacity={0.65} />
      <SparkStar cx={125} cy={45} size={2} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={75} cy={40} size={2.2} color={C.goldStrong} opacity={0.6} />
    </svg>
  );
}

/**
 * LUNA CON HOJAS — Acompañamiento espiritual / Coaching del alma.
 * Luna creciente dorada grande + ramitas de hojas orgánicas colgando + estrellas
 * (referencia: card "Acompañamiento espiritual").
 */
function ArtworkMoonWithLeaves(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Luna creciente con ramas doradas"
    >
      <WatercolorHalo />

      {/* Luna creciente dorada */}
      <defs>
        <radialGradient id="moon-gradient" cx="30%" cy="35%" r="70%">
          <stop offset="0%" stopColor={C.goldFaint} />
          <stop offset="100%" stopColor={C.goldStrong} />
        </radialGradient>
      </defs>
      <path
        d="M115 30 A 45 45 0 1 0 145 100 A 34 34 0 0 1 115 30 Z"
        fill="url(#moon-gradient)"
        stroke={C.goldStrong}
        strokeWidth="1"
      />

      {/* Rama izquierda descendente */}
      <path
        d="M78 100 Q 65 115 62 140"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.4"
        opacity="0.85"
        strokeLinecap="round"
      />
      {/* Hojas rama izquierda */}
      <ellipse
        cx="72"
        cy="118"
        rx="4"
        ry="8"
        transform="rotate(-30 72 118)"
        fill={C.goldSoft}
        opacity="0.8"
      />
      <ellipse
        cx="66"
        cy="132"
        rx="4"
        ry="7"
        transform="rotate(-40 66 132)"
        fill={C.violetSoft}
        opacity="0.6"
      />
      <ellipse
        cx="60"
        cy="140"
        rx="3"
        ry="6"
        transform="rotate(-15 60 140)"
        fill={C.goldSoft}
        opacity="0.75"
      />

      {/* Rama derecha descendente */}
      <path
        d="M130 100 Q 143 115 148 140"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.4"
        opacity="0.85"
        strokeLinecap="round"
      />
      {/* Hojas rama derecha */}
      <ellipse
        cx="140"
        cy="118"
        rx="4"
        ry="8"
        transform="rotate(30 140 118)"
        fill={C.goldSoft}
        opacity="0.8"
      />
      <ellipse
        cx="146"
        cy="132"
        rx="4"
        ry="7"
        transform="rotate(40 146 132)"
        fill={C.violetSoft}
        opacity="0.6"
      />
      <ellipse
        cx="152"
        cy="140"
        rx="3"
        ry="6"
        transform="rotate(15 152 140)"
        fill={C.goldSoft}
        opacity="0.75"
      />

      {/* Línea de horizonte sutil */}
      <path
        d="M40 148 Q 100 155 170 148"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="0.7"
        opacity="0.4"
      />

      {/* Estrellas */}
      <SparkStar cx={60} cy={40} size={4} color={C.goldStrong} />
      <SparkStar cx={155} cy={50} size={3} color={C.goldStrong} opacity={0.8} />
      <SparkStar cx={45} cy={72} size={2.5} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={170} cy={80} size={2.5} color={C.goldStrong} opacity={0.65} />
      <SparkStar cx={110} cy={35} size={2.2} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={30} cy={95} size={2} color={C.goldStrong} opacity={0.55} />
    </svg>
  );
}

/**
 * RAMAS CON HOJAS — Arteterapia.
 * Composición ornamental de ramas curvas doradas + hojas violeta/dorado en
 * abanico + destellos (referencia: card "Arteterapia").
 */
function ArtworkBranches(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Ramas doradas con hojas de acuarela"
    >
      <WatercolorHalo />

      {/* Rama principal central */}
      <path
        d="M100 150 Q 100 100 100 40"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.5"
        opacity="0.75"
        strokeLinecap="round"
      />

      {/* Ramitas secundarias */}
      <path
        d="M100 130 Q 78 118 55 100"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.2"
        opacity="0.7"
        strokeLinecap="round"
      />
      <path
        d="M100 110 Q 78 100 60 78"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.1"
        opacity="0.65"
        strokeLinecap="round"
      />
      <path
        d="M100 90 Q 78 75 68 55"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1"
        opacity="0.6"
        strokeLinecap="round"
      />

      <path
        d="M100 130 Q 122 118 145 100"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.2"
        opacity="0.7"
        strokeLinecap="round"
      />
      <path
        d="M100 110 Q 122 100 140 78"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.1"
        opacity="0.65"
        strokeLinecap="round"
      />
      <path
        d="M100 90 Q 122 75 132 55"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1"
        opacity="0.6"
        strokeLinecap="round"
      />

      {/* Hojas violeta */}
      <ellipse
        cx="60"
        cy="105"
        rx="7"
        ry="4"
        transform="rotate(-25 60 105)"
        fill={C.violetSoft}
        opacity="0.7"
      />
      <ellipse
        cx="65"
        cy="83"
        rx="6"
        ry="4"
        transform="rotate(-35 65 83)"
        fill={C.violet}
        opacity="0.55"
      />
      <ellipse
        cx="72"
        cy="60"
        rx="5"
        ry="3.5"
        transform="rotate(-45 72 60)"
        fill={C.violetSoft}
        opacity="0.65"
      />

      <ellipse
        cx="140"
        cy="105"
        rx="7"
        ry="4"
        transform="rotate(25 140 105)"
        fill={C.violetSoft}
        opacity="0.7"
      />
      <ellipse
        cx="135"
        cy="83"
        rx="6"
        ry="4"
        transform="rotate(35 135 83)"
        fill={C.violet}
        opacity="0.55"
      />
      <ellipse
        cx="128"
        cy="60"
        rx="5"
        ry="3.5"
        transform="rotate(45 128 60)"
        fill={C.violetSoft}
        opacity="0.65"
      />

      {/* Hojas doradas mezcladas */}
      <ellipse
        cx="82"
        cy="115"
        rx="6"
        ry="3.5"
        transform="rotate(-15 82 115)"
        fill={C.goldSoft}
        opacity="0.8"
      />
      <ellipse
        cx="118"
        cy="115"
        rx="6"
        ry="3.5"
        transform="rotate(15 118 115)"
        fill={C.goldSoft}
        opacity="0.8"
      />
      <ellipse
        cx="88"
        cy="93"
        rx="5"
        ry="3"
        transform="rotate(-20 88 93)"
        fill={C.goldSoft}
        opacity="0.7"
      />
      <ellipse
        cx="112"
        cy="93"
        rx="5"
        ry="3"
        transform="rotate(20 112 93)"
        fill={C.goldSoft}
        opacity="0.7"
      />

      {/* Puntas doradas superiores */}
      <circle cx="100" cy="35" r="3" fill={C.goldStrong} />
      <circle cx="68" cy="52" r="2" fill={C.goldStrong} opacity="0.8" />
      <circle cx="132" cy="52" r="2" fill={C.goldStrong} opacity="0.8" />

      {/* Destellos */}
      <SparkStar cx={45} cy={45} size={3} color={C.goldStrong} />
      <SparkStar cx={160} cy={50} size={2.5} color={C.goldStrong} opacity={0.8} />
      <SparkStar cx={35} cy={130} size={2.5} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={165} cy={135} size={2.5} color={C.goldStrong} opacity={0.7} />
    </svg>
  );
}

/**
 * SOL CON RAYOS — Limpieza energética.
 * Sol dorado central con cara sutil, rayos radiantes, ramitas laterales
 * (referencia: card "Limpieza energética").
 */
function ArtworkSun(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Sol dorado con rayos y ramas"
    >
      <WatercolorHalo />

      {/* Rayos */}
      <g stroke={C.goldStrong} strokeWidth="1.2" strokeLinecap="round" opacity="0.85">
        <line x1="100" y1="30" x2="100" y2="42" />
        <line x1="100" y1="118" x2="100" y2="130" />
        <line x1="45" y1="80" x2="57" y2="80" />
        <line x1="143" y1="80" x2="155" y2="80" />
        <line x1="60" y1="42" x2="68" y2="52" />
        <line x1="140" y1="42" x2="132" y2="52" />
        <line x1="60" y1="118" x2="68" y2="108" />
        <line x1="140" y1="118" x2="132" y2="108" />
      </g>
      <g stroke={C.goldStrong} strokeWidth="0.9" strokeLinecap="round" opacity="0.6">
        <line x1="100" y1="18" x2="100" y2="28" />
        <line x1="35" y1="80" x2="45" y2="80" />
        <line x1="155" y1="80" x2="165" y2="80" />
        <line x1="52" y1="32" x2="58" y2="42" />
        <line x1="148" y1="32" x2="142" y2="42" />
      </g>

      {/* Sol círculo con acuarela */}
      <defs>
        <radialGradient id="sun-grad" cx="45%" cy="40%" r="70%">
          <stop offset="0%" stopColor={C.goldFaint} />
          <stop offset="100%" stopColor={C.goldStrong} />
        </radialGradient>
      </defs>
      <circle
        cx="100"
        cy="80"
        r="30"
        fill="url(#sun-grad)"
        stroke={C.goldStrong}
        strokeWidth="1.2"
      />

      {/* Cara sutil dentro del sol */}
      <circle cx="90" cy="75" r="1.6" fill={C.violet} />
      <circle cx="110" cy="75" r="1.6" fill={C.violet} />
      <path
        d="M92 88 Q 100 93 108 88"
        fill="none"
        stroke={C.violet}
        strokeWidth="1"
        strokeLinecap="round"
      />

      {/* Ramitas curvas laterales inferiores */}
      <path
        d="M45 130 Q 65 138 78 128"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.1"
        opacity="0.8"
        strokeLinecap="round"
      />
      <path
        d="M155 130 Q 135 138 122 128"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.1"
        opacity="0.8"
        strokeLinecap="round"
      />
      <ellipse
        cx="55"
        cy="132"
        rx="3"
        ry="5"
        transform="rotate(-30 55 132)"
        fill={C.violetSoft}
        opacity="0.6"
      />
      <ellipse
        cx="145"
        cy="132"
        rx="3"
        ry="5"
        transform="rotate(30 145 132)"
        fill={C.violetSoft}
        opacity="0.6"
      />
      <ellipse
        cx="70"
        cy="128"
        rx="3"
        ry="5"
        transform="rotate(-10 70 128)"
        fill={C.goldSoft}
        opacity="0.7"
      />
      <ellipse
        cx="130"
        cy="128"
        rx="3"
        ry="5"
        transform="rotate(10 130 128)"
        fill={C.goldSoft}
        opacity="0.7"
      />

      {/* Destellos */}
      <SparkStar cx={45} cy={50} size={2.5} color={C.goldStrong} opacity={0.8} />
      <SparkStar cx={160} cy={55} size={2.5} color={C.goldStrong} opacity={0.75} />
      <SparkStar cx={38} cy={110} size={2} color={C.goldStrong} opacity={0.65} />
      <SparkStar cx={165} cy={112} size={2} color={C.goldStrong} opacity={0.65} />
    </svg>
  );
}

/**
 * LIBRO ABIERTO — Lectura de Registros Akáshicos / Tarot.
 * Libro en perspectiva 3/4 con páginas doradas y estrellas + destellos
 * (referencia: card "Lectura de Registros Akáshicos").
 */
function ArtworkBook(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Libro místico abierto"
    >
      <WatercolorHalo />

      {/* Sombra del libro debajo */}
      <ellipse cx="100" cy="140" rx="55" ry="6" fill={C.violet} opacity="0.15" />

      {/* Libro — página izquierda */}
      <path
        d="M100 60 L 60 70 L 55 128 L 100 118 Z"
        fill="#FBF3E0"
        stroke={C.goldStrong}
        strokeWidth="1"
      />
      {/* Libro — página derecha */}
      <path
        d="M100 60 L 140 70 L 145 128 L 100 118 Z"
        fill="#FDF7E6"
        stroke={C.goldStrong}
        strokeWidth="1"
      />

      {/* Líneas de texto en páginas */}
      <g stroke={C.goldStrong} strokeWidth="0.5" opacity="0.5">
        <line x1="65" y1="82" x2="93" y2="76" />
        <line x1="65" y1="90" x2="93" y2="84" />
        <line x1="65" y1="98" x2="93" y2="92" />
        <line x1="107" y1="76" x2="135" y2="82" />
        <line x1="107" y1="84" x2="135" y2="90" />
        <line x1="107" y1="92" x2="135" y2="98" />
      </g>

      {/* Estrella grande dorada en página derecha */}
      <SparkStar cx={120} cy={100} size={7} color={C.goldStrong} />
      <SparkStar cx={120} cy={100} size={3.5} color="#FFFFFF" opacity={0.8} />

      {/* Estrella pequeña página izquierda */}
      <SparkStar cx={80} cy={105} size={4} color={C.goldStrong} opacity={0.85} />

      {/* Contorno lomo del libro */}
      <line
        x1="100"
        y1="60"
        x2="100"
        y2="118"
        stroke={C.goldStrong}
        strokeWidth="0.8"
        opacity="0.7"
      />

      {/* Destellos alrededor */}
      <SparkStar cx={50} cy={45} size={3} color={C.goldStrong} opacity={0.8} />
      <SparkStar cx={155} cy={40} size={2.5} color={C.goldStrong} opacity={0.75} />
      <SparkStar cx={40} cy={85} size={2} color={C.goldStrong} opacity={0.6} />
      <SparkStar cx={165} cy={90} size={2} color={C.goldStrong} opacity={0.6} />
      <SparkStar cx={100} cy={30} size={3} color={C.goldStrong} opacity={0.7} />
    </svg>
  );
}

/**
 * LOTO VIBRACIONAL — Sanación con sonido / Sanación con cuencos.
 * Loto con arco superior + llama central que sugiere frecuencia + estrellas
 * (referencia: card "Sanación con sonido").
 */
function ArtworkSoundLotus(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Loto vibracional con arco dorado"
    >
      <WatercolorHalo />

      {/* Arco dorado superior */}
      <path
        d="M55 90 Q 100 30 145 90"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.4"
        strokeLinecap="round"
      />
      {/* Punta arco */}
      <circle cx="100" cy="28" r="2.5" fill={C.goldStrong} />

      {/* Llama central */}
      <path
        d="M100 118 Q 92 100 100 78 Q 108 100 100 118 Z"
        fill={C.goldSoft}
        stroke={C.goldStrong}
        strokeWidth="1"
        opacity="0.9"
      />
      <path d="M100 118 Q 96 108 100 90 Q 104 108 100 118 Z" fill={C.violetSoft} opacity="0.6" />

      {/* Base — pétalos loto */}
      <path
        d="M100 128 Q 80 122 68 100 Q 82 100 92 112 Q 100 120 100 128 Z"
        fill={C.violetSoft}
        opacity="0.5"
      />
      <path
        d="M100 128 Q 120 122 132 100 Q 118 100 108 112 Q 100 120 100 128 Z"
        fill={C.violetSoft}
        opacity="0.5"
      />
      <path
        d="M100 128 Q 88 120 82 105 Q 92 108 96 116 Q 100 122 100 128 Z"
        fill={C.violet}
        opacity="0.65"
      />
      <path
        d="M100 128 Q 112 120 118 105 Q 108 108 104 116 Q 100 122 100 128 Z"
        fill={C.violet}
        opacity="0.65"
      />
      {/* Pétalo central dorado */}
      <path d="M100 128 Q 96 118 100 108 Q 104 118 100 128 Z" fill={C.goldStrong} opacity="0.85" />

      {/* Línea base */}
      <path
        d="M60 132 Q 100 138 140 132"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="0.7"
        opacity="0.5"
      />

      {/* Destellos */}
      <SparkStar cx={55} cy={55} size={3} color={C.goldStrong} opacity={0.85} />
      <SparkStar cx={150} cy={60} size={2.5} color={C.goldStrong} opacity={0.75} />
      <SparkStar cx={40} cy={100} size={2.5} color={C.goldStrong} opacity={0.65} />
      <SparkStar cx={165} cy={105} size={2.5} color={C.goldStrong} opacity={0.65} />
      <SparkStar cx={80} cy={55} size={2} color={C.goldStrong} opacity={0.6} />
      <SparkStar cx={120} cy={50} size={2} color={C.goldStrong} opacity={0.6} />
    </svg>
  );
}

/**
 * LOTO ORNAMENTAL — Círculos y rituales.
 * Flor loto de 5 pétalos amplios violeta+dorado con arco superior y estrella
 * (referencia: card "Círculos y rituales").
 */
function ArtworkOrnamentalLotus(): ReactElement {
  return (
    <svg
      viewBox="0 0 200 180"
      className="h-full w-full"
      role="img"
      aria-label="Loto ornamental con arco"
    >
      <WatercolorHalo />

      {/* Arco superior + punto */}
      <path
        d="M60 65 Q 100 35 140 65"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="1.2"
        opacity="0.8"
        strokeLinecap="round"
      />
      <circle cx="100" cy="30" r="2.5" fill={C.goldStrong} />
      <line
        x1="100"
        y1="33"
        x2="100"
        y2="52"
        stroke={C.goldStrong}
        strokeWidth="0.9"
        opacity="0.7"
      />

      {/* Pétalos loto — 5 grandes */}
      {/* Pétalo central */}
      <path
        d="M100 135 Q 92 100 100 60 Q 108 100 100 135 Z"
        fill={C.violet}
        stroke={C.goldStrong}
        strokeWidth="0.9"
        opacity="0.75"
      />
      {/* Pétalos medios */}
      <path
        d="M100 135 Q 76 110 74 78 Q 88 82 96 100 Q 100 118 100 135 Z"
        fill={C.violetSoft}
        stroke={C.goldStrong}
        strokeWidth="0.8"
        opacity="0.7"
      />
      <path
        d="M100 135 Q 124 110 126 78 Q 112 82 104 100 Q 100 118 100 135 Z"
        fill={C.violetSoft}
        stroke={C.goldStrong}
        strokeWidth="0.8"
        opacity="0.7"
      />
      {/* Pétalos exteriores */}
      <path
        d="M100 135 Q 60 118 50 88 Q 68 88 82 102 Q 100 118 100 135 Z"
        fill={C.violetFaint}
        stroke={C.goldStrong}
        strokeWidth="0.7"
        opacity="0.55"
      />
      <path
        d="M100 135 Q 140 118 150 88 Q 132 88 118 102 Q 100 118 100 135 Z"
        fill={C.violetFaint}
        stroke={C.goldStrong}
        strokeWidth="0.7"
        opacity="0.55"
      />

      {/* Base sutil */}
      <path
        d="M55 138 Q 100 145 145 138"
        fill="none"
        stroke={C.goldStrong}
        strokeWidth="0.7"
        opacity="0.5"
      />

      {/* Destellos */}
      <SparkStar cx={50} cy={50} size={3} color={C.goldStrong} opacity={0.85} />
      <SparkStar cx={155} cy={55} size={2.5} color={C.goldStrong} opacity={0.75} />
      <SparkStar cx={40} cy={100} size={2.5} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={165} cy={105} size={2.5} color={C.goldStrong} opacity={0.7} />
      <SparkStar cx={70} cy={38} size={2} color={C.goldStrong} opacity={0.65} />
      <SparkStar cx={130} cy={38} size={2} color={C.goldStrong} opacity={0.65} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Selector por icon_key — con fallbacks razonables
// ---------------------------------------------------------------------------

/**
 * Mapea `focus_tags` de una terapia al `iconKey` que consume
 * `<TherapyArtwork>`. Se usa cuando el consumidor NO tiene acceso al
 * `category.icon_key` real (por ejemplo el bloque destacado del Home o
 * `TerapiaDestacada`, que reciben `Therapy` pero no `TherapyCategory[]`).
 *
 *   akashic/akásh → `mensaje` (libro místico)
 *   espiritual/alma → `luna` (luna con ramas doradas)
 *   energ → `ojo` (sol con rayos)
 *   creativ → `hoja` (rama ornamental con hojas)
 *   default → `loto` (loto con estrella, universal)
 */
export function focusToIconKey(focusTags: string[] | null | undefined): string {
  const lower = (focusTags ?? []).map((x) => x.toLowerCase());
  if (lower.some((x) => x.includes('akashic') || x.includes('akásh'))) {
    return 'mensaje';
  }
  if (lower.some((x) => x.includes('espiritual') || x.includes('alma'))) {
    return 'luna';
  }
  if (lower.some((x) => x.includes('energ'))) return 'ojo';
  if (lower.some((x) => x.includes('creativ'))) return 'hoja';
  return 'loto';
}

/**
 * Selecciona la ilustración según el `icon_key` de la categoría.
 * Coberage: `loto`, `luna`, `hoja`, `ojo`, `mensaje`, `corazon`, `bolsa` y
 * cualquier valor desconocido → cae al loto con estrella (motivo universal).
 */
export function TherapyArtwork({ iconKey }: { iconKey?: string | null }): ReactElement {
  const key = iconKey?.toLowerCase() ?? '';
  switch (key) {
    case 'luna':
      return <ArtworkMoonWithLeaves />;
    case 'hoja':
      return <ArtworkBranches />;
    case 'ojo':
      return <ArtworkSun />;
    case 'mensaje':
      return <ArtworkBook />;
    case 'corazon':
      return <ArtworkSoundLotus />;
    case 'bolsa':
      return <ArtworkOrnamentalLotus />;
    case 'loto':
    case 'estrella':
    case 'calendario':
    default:
      return <ArtworkLotusStar />;
  }
}
